import type { McpHostedDeployment, McpSeatType, McpServer, McpServerConnection } from "../api/mcp";

// Presentation rules for the MCP Server tab and the pairing page. Kept free of Svelte
// so they are unit-testable and shared by every sub-view.

/** The one command that pairs a customer-hosted MCP server with the portal. */
export const MCP_SETUP_COMMAND = "epidev-mcp setup";
export const MCP_PAIR_PATH = "/mcp/pair";
/**
 * The public setup guide on epidev.ai. The site publishes clean directory paths
 * (`/wiki/`, `/plan-comparison`), never `.html` files, so the portal links the
 * directory form.
 */
export const MCP_SETUP_GUIDE_URL = "https://epidev.ai/wiki/hosted-mcp";

/** A tab of this organization's MCP Server page. */
export function mcpTabHref(orgId: string, tab: "servers" | "seats" | "permissions" | "environments" | "usage"): string {
  const base = `/orgs/${encodeURIComponent(orgId)}/mcp`;
  return tab === "servers" ? base : `${base}?tab=${tab}`;
}

export type McpTone = "ok" | "pending" | "error" | "neutral" | "revoked";

/**
 * One status line per server. `status` is the portal's registration state; link
 * liveness comes from persisted connection rows (`linkState`), never a socket guess.
 */
export function mcpServerState(server: Pick<McpServer, "status" | "linkState">): { label: string; tone: McpTone; detail: string } {
  if (server.status === "revoked") {
    return { label: "Unlinked", tone: "revoked", detail: "This server was unlinked and no longer holds a license." };
  }
  if (server.status === "pending") {
    return {
      label: "Awaiting setup",
      tone: "pending",
      detail: "Approved in the portal. Setup on the host has not collected its credentials yet."
    };
  }
  if (server.linkState === "online") return { label: "Connected", tone: "ok", detail: "The server holds an open link to the portal." };
  if (server.linkState === "never") {
    return { label: "Not connected yet", tone: "pending", detail: "Paired, but the server has not opened its portal link yet." };
  }
  return { label: "Offline", tone: "error", detail: "The server's last portal link closed. It reconnects on its own when it can." };
}

const CLOSE_REASONS: Record<number, string> = {
  1000: "Closed normally",
  1008: "Refused: invalid signature or frame",
  1012: "Portal restarted",
  4000: "Superseded by a newer connection",
  4001: "Heartbeat timed out",
  4003: "Revoked"
};

/** Human copy for a closed connection; an open one reads "Connected". */
export function mcpCloseReason(connection: Pick<McpServerConnection, "disconnectedAt" | "closeCode" | "closeReason">): string {
  if (!connection.disconnectedAt) return "Connected";
  if (connection.closeCode !== null && CLOSE_REASONS[connection.closeCode]) return CLOSE_REASONS[connection.closeCode]!;
  if (connection.closeReason) return connection.closeReason;
  return connection.closeCode === null ? "Closed" : `Closed (${connection.closeCode})`;
}

/** Duration between two instants as "3 h 12 min", "4 min", or "under a minute". */
export function mcpConnectionDuration(from: string, to: string | null, now = Date.now()): string {
  const end = to ? Date.parse(to) : now;
  const minutes = Math.max(0, Math.floor((end - Date.parse(from)) / 60_000));
  if (minutes < 1) return "under a minute";
  const days = Math.floor(minutes / 1_440);
  const hours = Math.floor((minutes % 1_440) / 60);
  const rest = minutes % 60;
  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  if (hours > 0) return rest > 0 ? `${hours} h ${rest} min` : `${hours} h`;
  return `${rest} min`;
}

const USER_CODE_ALPHABET = /^[BCDFGHJKLMNPQRSTVWXZ]{8}$/;

/**
 * Normalizes what a person types (`bcdf ghjk`, `BCDF-GHJK`) to the display form, or
 * null when it cannot be a pairing code. Mirrors the API's RFC 8628 alphabet.
 */
export function normalizePairingCode(value: string | null | undefined): string | null {
  const compact = (value ?? "").toUpperCase().replace(/[\s-]+/g, "");
  if (!USER_CODE_ALPHABET.test(compact)) return null;
  return `${compact.slice(0, 4)}-${compact.slice(4)}`;
}

/** Servers an admin may pick to retire when every license is in use. */
export function replaceableServers(servers: readonly McpServer[]): McpServer[] {
  return servers.filter((server) => server.status !== "revoked");
}

// ---- Seats (MCP_P_06) ---------------------------------------------------------------

type McpSeatCandidate = import("../api/mcp").McpSeatCandidate;
type McpSeatLicense = import("../api/mcp").McpSeatLicense;

/** Seat and server-license usage for the summary card. */
export function mcpSeatUsage(license: Pick<McpSeatLicense, "seatLimit" | "assigned" | "available" | "serverLimit" | "linkedServers">): {
  seats: string;
  seatsDetail: string;
  servers: string;
  serversDetail: string;
  full: boolean;
  overCapacityBy: number;
} {
  const overCapacityBy = Math.max(0, license.assigned - license.seatLimit);
  return {
    seats: `${license.assigned} of ${license.seatLimit}`,
    seatsDetail: overCapacityBy > 0
      ? `${overCapacityBy} more assigned than licensed`
      : license.available === 1 ? "1 seat available" : `${license.available} seats available`,
    servers: `${license.linkedServers} of ${license.serverLimit}`,
    serversDetail: license.linkedServers >= license.serverLimit && license.serverLimit > 0 ? "Every server license is in use" : "Server licenses linked",
    full: license.available <= 0,
    overCapacityBy
  };
}

/**
 * The two kinds of MCP seat. A read-write seat includes every read a read-only seat can
 * do, so nobody needs both. Prices are the published list prices; the invoice is Stripe's.
 */
export const MCP_SEAT_TYPES: ReadonlyArray<{ value: McpSeatType; label: string; price: string; summary: string }> = [
  {
    value: "readwrite",
    label: "Read-write",
    price: "$30 per user / month",
    summary: "Everything a read-only seat can do, plus creating, updating, deleting, and running actions in the ERP, within the permissions you publish."
  },
  {
    value: "readonly",
    label: "Read-only",
    price: "$5 per user / month",
    summary: "Reads ERP data only. Writes are refused for this seat even where the published permissions would allow them."
  }
];

/** A seat assigned before typed licensing carries no type and is read-write. */
export function mcpSeatTypeOf(seat: { seatType?: McpSeatType | null }): McpSeatType {
  return seat.seatType === "readonly" ? "readonly" : "readwrite";
}

export function mcpSeatTypeLabel(type: McpSeatType): string {
  return MCP_SEAT_TYPES.find((option) => option.value === type)!.label;
}

export function mcpSeatTypePrice(type: McpSeatType): string {
  return MCP_SEAT_TYPES.find((option) => option.value === type)!.price;
}

export interface McpSeatTypeUsage {
  type: McpSeatType;
  label: string;
  price: string;
  /** "2 of 3", for a stat tile. */
  value: string;
  detail: string;
  assigned: number;
  limit: number;
  available: number;
  full: boolean;
  overCapacityBy: number;
}

/** Capacity per seat type, in the order the type chooser offers them. */
export function mcpSeatTypeUsage(
  license: Pick<McpSeatLicense, "readWriteSeatLimit" | "readOnlySeatLimit" | "readWriteAssigned" | "readOnlyAssigned">
): McpSeatTypeUsage[] {
  return MCP_SEAT_TYPES.map((option) => {
    const limit = option.value === "readwrite" ? license.readWriteSeatLimit : license.readOnlySeatLimit;
    const assigned = option.value === "readwrite" ? license.readWriteAssigned : license.readOnlyAssigned;
    const available = Math.max(0, limit - assigned);
    const overCapacityBy = Math.max(0, assigned - limit);
    return {
      type: option.value,
      label: option.label,
      price: option.price,
      value: `${assigned} of ${limit}`,
      detail: overCapacityBy > 0
        ? `${overCapacityBy} more assigned than licensed`
        : available === 1 ? "1 seat available" : `${available} seats available`,
      assigned,
      limit,
      available,
      full: available <= 0,
      overCapacityBy
    };
  });
}

/** Case-insensitive match on name or email, in the API's order. */
export function filterSeatCandidates(members: readonly McpSeatCandidate[], query: string): McpSeatCandidate[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...members];
  return members.filter((member) => member.displayName.toLowerCase().includes(needle) || member.email.toLowerCase().includes(needle));
}

/**
 * The connect instructions a seat holder needs, built in the browser from the portal URL
 * they are using (nothing is emailed). `epidev-mcp setup` pairs or re-pairs the host and
 * `--portal-url` points it at this portal; MCP_M_07 owns the CLI flag.
 */
export function mcpConnectInstructions(input: { portalUrl: string; orgId: string; orgName: string; email: string }): string {
  const portal = input.portalUrl.replace(/\/+$/, "");
  return [
    `# EpiDev MCP for ${input.orgName}: seat for ${input.email}`,
    `epidev-mcp setup --portal-url ${portal}`,
    `# Servers, seats, and permissions: ${portal}/orgs/${input.orgId}/mcp`
  ].join("\n");
}

export type McpSeatErrorKind = "capacity" | "conflict" | "license" | "other";

/** Which recovery the Seats view offers for a failed seat write (codes are Entitlements' own). */
export function mcpSeatErrorKind(code: string): McpSeatErrorKind {
  if (code === "seat_capacity_exceeded") return "capacity";
  if (code === "version_conflict" || code === "precondition_required") return "conflict";
  if (code === "mcp_license_required") return "license";
  return "other";
}

// ---- Hosted MCP (self-service provisioning) -----------------------------------------

/** The card refreshes itself while a deployment is provisioning, then gives up and offers a manual refresh. */
export const MCP_HOSTED_POLL_MS = 3_000;
export const MCP_HOSTED_POLL_ATTEMPTS = 12;
/** The API's own limit (`hostedServerName`); it trims and refuses control characters. */
export const MCP_HOSTED_NAME_MAX = 120;
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;

/**
 * Why this name cannot be sent, or null when it can. Deliberately no uniqueness check:
 * the API resumes a failed deployment of the same name rather than taking another
 * licence, so refusing a repeat here would block the retry it is built for.
 */
export function mcpHostedNameIssue(name: string): string | null {
  const value = name.trim();
  if (!value) return "Give the server a name, for example Acme production.";
  if (value.length > MCP_HOSTED_NAME_MAX) return `Use at most ${MCP_HOSTED_NAME_MAX} characters.`;
  if (CONTROL_CHARACTERS.test(name)) return "Leave out line breaks and other control characters.";
  return null;
}

/**
 * One status line per hosted deployment. `ready` is the backend's word, never the
 * browser's: the card says the server is ready only when the API says so.
 */
export function mcpHostedState(deployment: Pick<McpHostedDeployment, "status" | "errorCode">): { label: string; tone: McpTone; detail: string } {
  switch (deployment.status) {
    case "ready":
      return { label: "Ready", tone: "ok", detail: "EpiDev is running this MCP server and it is linked to this organization." };
    case "failed":
      return { label: "Provisioning failed", tone: "error", detail: mcpHostedErrorMessage(deployment.errorCode) };
    case "stopped":
      return { label: "Stopped", tone: "revoked", detail: "This hosted server was stopped. Create a new server to use the released license." };
    default:
      return {
        label: "Provisioning",
        tone: "pending",
        detail: "EpiDev is creating this MCP server. It usually takes a few minutes; this page checks again on its own."
      };
  }
}

/** Copy for a failed deployment's `errorCode`; an unknown code is still named for support. */
export function mcpHostedErrorMessage(code: string | null | undefined): string {
  switch (code) {
    case "capacity_unavailable":
      return "EpiDev had no capacity to place the server. Nothing is running and nothing was billed. Try again in a few minutes.";
    case "name_conflict":
      return "Another hosted server in this organization already uses that name. Try again with a different name.";
    case "provision_timeout":
      return "The server did not finish starting in time. Nothing is running yet. Try again, or contact EpiDev support if it keeps failing.";
    case "server_limit_exceeded":
      return "Every MCP server license is in use. Unlink a server or add a license from Billing, then try again.";
    case "mcp_license_required":
      return "An active MCP server license is required before EpiDev can host a server for this organization.";
    default:
      return `EpiDev recorded ${code ? `"${code}"` : "no reason"}. Setup did not finish. Retry the same server name, or contact EpiDev support with that code.`;
  }
}

/** The deployment the card speaks for: the newest one that is not stopped, else the newest. */
export function mcpHostedPrimary(deployments: readonly McpHostedDeployment[]): McpHostedDeployment | null {
  if (deployments.length === 0) return null;
  const rank: Record<McpHostedDeployment["status"], number> = { provisioning: 0, failed: 1, ready: 2, stopped: 3 };
  return [...deployments].sort((a, b) => rank[a.status] - rank[b.status])[0] ?? null;
}

export function mcpHostedProvisioning(deployments: readonly McpHostedDeployment[]): boolean {
  return deployments.some((deployment) => deployment.status === "provisioning");
}

export interface McpNextStep {
  id: string;
  title: string;
  detail: string;
  href: string;
  linkLabel: string;
}

/** What an owner does once a hosted server exists, in the order it has to happen. */
export function mcpHostedNextSteps(orgId: string): McpNextStep[] {
  return [
    {
      id: "environments",
      title: "Add your ERP environments",
      detail: "Point the server at Epicor and enter the credentials. They are sealed in your browser to this server and the portal never stores them.",
      href: mcpTabHref(orgId, "environments"),
      linkLabel: "Open Environments"
    },
    {
      id: "seats",
      title: "Assign MCP seats",
      detail: "Everyone who uses the server needs a seat, owners and admins included. Choose read-write or read-only for each person.",
      href: mcpTabHref(orgId, "seats"),
      linkLabel: "Open Seats"
    },
    {
      id: "permissions",
      title: "Publish permissions",
      detail: "Decide which tools and environments the server exposes. Nobody can sign in until a policy is published.",
      href: mcpTabHref(orgId, "permissions"),
      linkLabel: "Open Permissions"
    },
    {
      id: "connect",
      title: "Connect ChatGPT, Claude, or your CLI",
      detail: "Add the server's URL to each app, then sign in with your portal account. The steps for each app are on this tab.",
      href: mcpTabHref(orgId, "servers"),
      linkLabel: "Back to Servers"
    }
  ];
}

// ---- Usage (MCP_P_08) ---------------------------------------------------------------

type McpUsageEvent = import("../api/mcp").McpUsageEvent;
type McpUsageQuery = import("../api/mcp").McpUsageQuery;
type McpUsageOutcome = import("../api/mcp").McpUsageOutcome;
type McpWriteClass = import("../api/mcp").McpWriteClass;

export type McpUsageRange = "24h" | "7d" | "30d" | "custom";

export const MCP_USAGE_RANGES: ReadonlyArray<{ value: McpUsageRange; label: string }> = [
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "custom", label: "Custom range" }
];

export const MCP_WRITE_CLASS_OPTIONS: ReadonlyArray<McpWriteClass> = ["create", "update", "delete", "execute", "transfer", "files", "security"];

export const MCP_OUTCOME_LABELS: Record<McpUsageOutcome, string> = {
  ok: "Completed",
  error: "Failed",
  refused: "Refused",
  cancelled: "Cancelled"
};

/** The API's limit on a CSV export or summary window. */
export const MCP_USAGE_MAX_RANGE_DAYS = 92;

export interface McpUsageFilterState {
  range: McpUsageRange;
  /** `YYYY-MM-DD`, local calendar days, both inclusive. Used when range is custom. */
  customFrom: string;
  customTo: string;
  subject: string;
  env: string;
  tool: string;
  decision: "" | "allow" | "deny";
  writeClass: "" | McpWriteClass;
  outcome: "" | McpUsageOutcome;
}

export function emptyMcpUsageFilters(): McpUsageFilterState {
  return { range: "7d", customFrom: "", customTo: "", subject: "", env: "", tool: "", decision: "", writeClass: "", outcome: "" };
}

const RANGE_MS: Record<Exclude<McpUsageRange, "custom">, number> = { "24h": 86_400_000, "7d": 7 * 86_400_000, "30d": 30 * 86_400_000 };

function localDay(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const at = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
  return Number.isNaN(at) ? null : at;
}

/** Why a custom range cannot be applied yet, or null when it can (or is not custom). */
export function mcpUsageRangeProblem(state: Pick<McpUsageFilterState, "range" | "customFrom" | "customTo">): string | null {
  if (state.range !== "custom") return null;
  const from = localDay(state.customFrom);
  const to = localDay(state.customTo);
  if (from === null || to === null) return "Choose both a start and an end date.";
  if (from > to) return "The start date must be on or before the end date.";
  if ((to - from) / 86_400_000 + 1 > MCP_USAGE_MAX_RANGE_DAYS) return `A range may cover at most ${MCP_USAGE_MAX_RANGE_DAYS} days.`;
  return null;
}

/**
 * The API query for the filter bar. Presets are open-ended (`from` only) so the newest
 * events keep appearing; a custom range ends at the start of the day after `customTo`.
 */
export function mcpUsageQuery(state: McpUsageFilterState, now = Date.now()): McpUsageQuery {
  const query: McpUsageQuery = {};
  const text = (value: string) => value.trim();
  if (text(state.subject)) query.subject = text(state.subject);
  if (text(state.env)) query.env = text(state.env);
  if (text(state.tool)) query.tool = text(state.tool);
  if (state.decision) query.decision = state.decision;
  if (state.writeClass) query.writeClass = state.writeClass;
  if (state.outcome) query.outcome = state.outcome;
  if (state.range === "custom") {
    const from = localDay(state.customFrom);
    const to = localDay(state.customTo);
    if (from !== null && to !== null && from <= to) {
      query.from = new Date(from).toISOString();
      const end = new Date(to);
      end.setDate(end.getDate() + 1);
      query.to = end.toISOString();
    }
  } else {
    query.from = new Date(now - RANGE_MS[state.range]).toISOString();
  }
  return query;
}

/** Filters other than the time range that are set (for "Clear filters"). */
export function mcpUsageActiveFilters(state: McpUsageFilterState): number {
  return [state.subject.trim(), state.env.trim(), state.tool.trim(), state.decision, state.writeClass, state.outcome].filter(Boolean).length;
}

/** The tool column: resource reads carry no tool name. */
export function mcpUsageToolLabel(event: Pick<McpUsageEvent, "tool" | "method">): string {
  if (event.tool) return event.tool;
  return event.method === "resources/read" ? "Resource read" : "Unknown tool";
}

/** The client-supplied clientInfo label ("claude-code 2.1.4"), or null. Never authorization. */
export function mcpUsageClientLabel(event: Pick<McpUsageEvent, "clientName" | "clientVersion">): string | null {
  const name = event.clientName?.trim();
  if (!name) return null;
  const version = event.clientVersion?.trim();
  return version ? `${name} ${version}` : name;
}

/** The OAuth sign-in a call came from, in the Connected apps "…suffix" style, or null. */
export function mcpUsageSignInLabel(authSessionId: string | null | undefined): string | null {
  return authSessionId ? `…${authSessionId.slice(-6)}` : null;
}

export function mcpUsageAccessLabel(event: Pick<McpUsageEvent, "access" | "writeClass">): string {
  if (event.access === "write") return event.writeClass ? `Write · ${event.writeClass}` : "Write";
  if (event.access === "read") return "Read";
  return "Unclassified";
}

export function mcpUsageOutcomeLabel(outcome: McpUsageEvent["outcome"]): string {
  return outcome ? MCP_OUTCOME_LABELS[outcome] ?? outcome : "—";
}

export function mcpUsageDecisionTone(decision: McpUsageEvent["decision"]): "ok" | "error" {
  return decision === "deny" ? "error" : "ok";
}

export function mcpUsageDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1_000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(ms < 10_000 ? 1 : 0)} s`;
  return `${Math.floor(ms / 60_000)} min ${Math.round((ms % 60_000) / 1_000)} s`;
}

/** 0.2537 → "25.4%"; exact zero and one read without a decimal. */
export function mcpDenialRate(rate: number): string {
  if (rate <= 0) return "0%";
  if (rate >= 1) return "100%";
  return `${(rate * 100).toFixed(1)}%`;
}

/** Copy for a refused export; the API's own detail is shown alongside. */
export function mcpUsageExportProblem(code: string): string | null {
  if (code === "usage_export_too_large") return "Too many events to export at once";
  if (code === "usage_range_too_wide") return `Exports cover at most ${MCP_USAGE_MAX_RANGE_DAYS} days`;
  if (code === "rate_limited") return "Too many exports";
  return null;
}

// ---- Environments (MCP_P_07) --------------------------------------------------------

type McpEnvironmentRow = import("../api/mcp").McpEnvironment;
type McpEnvironmentMetadataInput = import("../api/mcp").McpEnvironmentMetadataInput;

export const MCP_ENVIRONMENT_NAME = /^[A-Za-z0-9][A-Za-z0-9 ._()-]{0,63}$/;
/** epidev/mcp-link/v1 company and plant codes. */
export const MCP_ENVIRONMENT_CODE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,31}$/;
/** epidev/mcp-link/v1 base URL: a plain host, an optional port, a path. No credentials, query, or fragment. */
export const MCP_ENVIRONMENT_BASE_URL = /^https?:\/\/[A-Za-z0-9.-]+(?::[0-9]{1,5})?(?:\/[A-Za-z0-9._~%!$&'()*+,;=:@/-]*)?$/;

export const MCP_MODE_OPTIONS: ReadonlyArray<{ value: "readonly" | "writable" | "live"; label: string }> = [
  { value: "readonly", label: "Read-only" },
  { value: "writable", label: "Writable" },
  { value: "live", label: "Live (production, never agent writes)" }
];

export const MCP_AGENT_WRITE_OPTIONS: ReadonlyArray<{ value: "deny" | "confirm" | "allow"; label: string }> = [
  { value: "deny", label: "Deny" },
  { value: "confirm", label: "Confirm each write" },
  { value: "allow", label: "Allow" }
];

export function mcpEnvironmentModeLabel(environment: Pick<McpEnvironmentRow, "mode" | "agentWrites">): string {
  const mode = environment.mode === "readonly" ? "Read-only" : environment.mode === "writable" ? "Writable" : "Live";
  const writes = environment.agentWrites === "deny" ? "agent writes denied" : environment.agentWrites === "confirm" ? "agent writes need confirmation" : "agent writes allowed";
  return `${mode}, ${writes}`;
}

/** The status column: what the portal last heard from the server about this environment. */
export function mcpEnvironmentStatus(environment: Pick<McpEnvironmentRow, "credential" | "validatedAt">): { label: string; tone: McpTone; detail: string } {
  if (!environment.credential) {
    return { label: "No credentials", tone: "neutral", detail: "The server holds no credentials for this environment yet, so it has not been validated." };
  }
  if (environment.validatedAt) return { label: "Validated", tone: "ok", detail: "The server signed in to the ERP with these credentials." };
  return { label: "Not validated", tone: "pending", detail: "Credentials are stored on the server but the connection has not been checked." };
}

export interface McpEnvironmentDraft {
  name: string;
  baseUrl: string;
  company: string;
  plant: string;
  mode: "readonly" | "writable" | "live";
  agentWrites: "deny" | "confirm" | "allow";
  allowSelfSignedCert: boolean;
}

export function mcpEnvironmentDraft(environment: McpEnvironmentRow | null): McpEnvironmentDraft {
  return environment
    ? {
        name: environment.name, baseUrl: environment.baseUrl, company: environment.company ?? "", plant: environment.plant ?? "",
        mode: environment.mode, agentWrites: environment.agentWrites, allowSelfSignedCert: environment.allowSelfSignedCert
      }
    : { name: "", baseUrl: "", company: "", plant: "", mode: "readonly", agentWrites: "deny", allowSelfSignedCert: false };
}

/** Field problems for the drawer, keyed by field; empty when the draft can be sent. */
export function mcpEnvironmentDraftIssues(draft: McpEnvironmentDraft, existingNames: readonly string[] = []): Record<string, string> {
  const issues: Record<string, string> = {};
  if (!MCP_ENVIRONMENT_NAME.test(draft.name) || draft.name !== draft.name.trim()) {
    issues.name = "Use 1-64 letters, digits, spaces, dots, dashes, underscores, or parentheses, starting with a letter or digit.";
  } else if (existingNames.some((name) => name.toLowerCase() === draft.name.toLowerCase())) {
    issues.name = "An environment with this name already exists. Edit it instead.";
  }
  const baseUrl = draft.baseUrl.trim();
  if (!baseUrl) issues.baseUrl = "Enter the ERP base URL the server should use.";
  else if (baseUrl.length > 2048 || !MCP_ENVIRONMENT_BASE_URL.test(baseUrl)) {
    issues.baseUrl = "Use an http(s) URL with a plain host name, an optional port, and a path, without credentials, a query, or a fragment.";
  }
  if (draft.company.trim() && !MCP_ENVIRONMENT_CODE.test(draft.company.trim())) issues.company = "Use 1-32 letters, digits, dashes, or underscores.";
  if (draft.plant.trim() && !MCP_ENVIRONMENT_CODE.test(draft.plant.trim())) issues.plant = "Use 1-32 letters, digits, dashes, or underscores.";
  if (draft.mode === "live" && draft.agentWrites !== "deny") issues.agentWrites = "A live environment always denies agent writes.";
  return issues;
}

/** The `environment` member of the PUT body (metadata only; never credentials). */
export function mcpEnvironmentPayload(draft: McpEnvironmentDraft): McpEnvironmentMetadataInput & { name: string } {
  return {
    name: draft.name,
    baseUrl: draft.baseUrl.trim(),
    company: draft.company.trim() || null,
    plant: draft.plant.trim() || null,
    mode: draft.mode,
    agentWrites: draft.mode === "live" ? "deny" : draft.agentWrites,
    allowSelfSignedCert: draft.allowSelfSignedCert
  };
}

export interface McpEnvironmentMessage {
  title: string;
  detail: string;
}

const NOTHING_SAVED = "Nothing was saved on the server or in the portal.";

/**
 * User-facing copy for the server's `env.result` validation codes (EpiDevMCP
 * src/standalone/portal-link.js). Unknown codes fall back to a generic refusal that
 * still names the code, so support can match it to the server log.
 */
export function mcpEnvironmentResultMessage(code: string | null | undefined): McpEnvironmentMessage {
  switch (code) {
    case "config_not_persistent":
      return { title: "The server has no config file to save to", detail: `It was started without a config file (for example from environment variables only). Start it with its epidev-mcp.json, then try again. ${NOTHING_SAVED}` };
    case "config_not_writable":
      return { title: "The server can't write its config file", detail: `epidev-mcp.json is read-only for the account the server runs as. Fix its permissions on the host, then try again. ${NOTHING_SAVED}` };
    case "config_unreadable":
      return { title: "The server couldn't read its config file", detail: `Check that epidev-mcp.json exists and is valid JSON on the host (epidev-mcp status reports it). ${NOTHING_SAVED}` };
    case "config_invalid":
      return { title: "The server refused this configuration", detail: `With this change its epidev-mcp.json would not be valid. Check the base URL and settings, or run epidev-mcp status on the host. ${NOTHING_SAVED}` };
    case "config_write_failed":
    case "save_failed":
      return { title: "The server couldn't save the change", detail: `Writing its configuration failed on the host; check the disk and the server log. ${NOTHING_SAVED}` };
    case "secret_store_unavailable":
    case "secret_store_failed":
      return { title: "The server's credential store is unavailable", detail: `The server could not open or write its local secret store. Check the state directory on the host. ${NOTHING_SAVED}` };
    case "recipient_mismatch":
      return { title: "Sealed to a different server key", detail: `The connected server holds a different sealing key than the one the portal has on record, for example after a reinstall. Re-pair the server, compare the fingerprints, and enter the credentials again. ${NOTHING_SAVED}` };
    case "credentials_unreadable":
      return { title: "The server couldn't open the credentials", detail: `The sealed credentials did not decrypt on the server. Reload this panel, compare the fingerprint, and enter them again. ${NOTHING_SAVED}` };
    case "credentials_invalid":
    case "credentials_incomplete":
      return { title: "The server rejected the credentials' format", detail: `A password and an API key are required, and none of the fields may contain line breaks. ${NOTHING_SAVED}` };
    case "credentials_missing":
      return { title: "No credentials on the server yet", detail: "The settings were saved, but the server holds no credentials for this environment, so the connection was not checked. Add credentials to validate it." };
    case "erp_auth_failed":
      return { title: "Epicor rejected the sign-in", detail: `The ERP did not accept this user name, password, or API key. ${NOTHING_SAVED}` };
    case "erp_unreachable":
      return { title: "The server couldn't reach Epicor", detail: `Check the base URL, DNS, and firewall from the MCP server's host (not from your browser). ${NOTHING_SAVED}` };
    case "erp_timeout":
      return { title: "Epicor didn't answer in time", detail: `The ERP took longer than 30 seconds to answer the server's check. Try again, or check the ERP's health. ${NOTHING_SAVED}` };
    case "erp_license_unavailable":
      return { title: "Epicor had no license for this session", detail: `The ERP refused the session for licensing. Free a license or use an integration account, then try again. ${NOTHING_SAVED}` };
    case "erp_version_unrecognized":
      return { title: "This doesn't look like an Epicor server", detail: `The base URL answered, but the server could not read an Epicor version from it. Check that the URL points at the Epicor application server. ${NOTHING_SAVED}` };
    case "erp_http_error":
      return { title: "Epicor answered with an error", detail: `The ERP returned an HTTP error to the server's check. Check the base URL and the ERP's logs. ${NOTHING_SAVED}` };
    case "erp_probe_failed":
      return { title: "The connection check failed", detail: `The server could not complete its check against the ERP. The server log has the detail. ${NOTHING_SAVED}` };
    case "busy":
      return { title: "The server is busy", detail: `It is still saving other environment changes. Try again in a moment. ${NOTHING_SAVED}` };
    default:
      return { title: "The server refused the change", detail: `The server answered ${code ? `"${code}"` : "without a reason"}. ${NOTHING_SAVED}` };
  }
}

/** Copy for API problems the Environments drawer can meet, or null to use the API's own detail. */
export function mcpEnvironmentProblemMessage(code: string): McpEnvironmentMessage | null {
  switch (code) {
    case "server_offline":
      return { title: "Not sent: the server is offline", detail: "The portal sends environment changes only to a connected server and never queues credentials. Start the server (epidev-mcp status shows its portal link), then save again. Nothing was saved." };
    case "sealing_key_mismatch":
      return { title: "The server's key changed", detail: "The credentials were sealed to a key this server no longer holds. Close this panel, reopen it to load the current key, compare the fingerprint, and enter the credentials again." };
    case "mcp_link_timeout":
      return { title: "The server didn't answer in time", detail: "The outcome is unknown: the server may still apply the change. Reload the list in a minute before trying again." };
    case "mcp_link_closed":
      return { title: "The server disconnected before answering", detail: "The outcome is unknown: the server may have applied the change. Reload the list once it reconnects." };
    case "mcp_link_busy":
      return { title: "The server is busy", detail: "It has too many requests in flight. Try again shortly. Nothing was saved." };
    case "version_conflict":
      return { title: "Someone else changed this environment", detail: "Close this panel and reopen it to load the latest settings." };
    case "mcp_server_revoked":
      return { title: "This server was unlinked", detail: "Environments can only be changed on a linked server." };
    default:
      return null;
  }
}
